import React, { useEffect, useRef, useState } from "react";
import AddButton from "../buttons/button";
import ItemsInputField from "../forms/items-input-field";
import ItemsSelectField from "../forms/items-select-field";
import UploadIcon from "../../assets/icons/upload-icon";
import ViewIcon from "../../assets/icons/view-icon";
import { useFormikContext } from "formik";
import { ChevronUp, ChevronDown } from "lucide-react";
import { handleShowErrorToast } from "../helper-components/helper-function";
import BillingUploadPrescription from "./billing-upload-prescription";
import { useNavigate } from "react-router-dom";
import errorToast from "../helper-components/error-toast";
import SearchField from "../forms/search-field";
import { doctorTableAction } from "../../redux/actions/doctor-action";
import { useDispatch } from "react-redux";
import TextSearchField from "../forms/text-select-field";
import {
  invoiceDraftCreateAction,
  invoiceEditAction,
} from "../../redux/actions/invoices-action";
import successToast from "../helper-components/success-toast";
import moment from "moment";

const BillingFooterSection = ({
  docList = [],
  disabled,
  page = "create",
  billCreateInputs = [],
  user_discount = {},
  formValuesRef,
  handleBilling,
  searchRef,
  storeId,
  clickDraftRef
}) => {
  const header_inv_dis_per_ref = useRef();
  const { values, setFieldValue, setFieldError, errors, validateForm } =
    useFormikContext();
  console.log(values, "dsfdsfsdfsdfsdfsdf");
  const [isOpen, setIsOpen] = useState(false);
  const [uploadPrescription, setUploadPrescription] = useState(false);
  const [uploadPrescriptionView, setUploadPrescriptionView] = useState(false);
  const [localData, setLocalData] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Auto-recalculate totals when data changes
  useEffect(() => {
    if (values?.data && values.data.length > 0) {
      // Recalculate totals whenever data changes
      const sot_total_gst = values.data.reduce(
        (total, item) => total + parseFloat(item?.item_tax_amount || 0),
        0
      );
      const sot_total_discount = values.data.reduce(
        (total, item) => total + parseFloat(item?.item_discount_amount || 0),
        0
      );
      const sot_sub_total = values.data.reduce(
        (subtotal, item) =>
          subtotal +
          parseFloat(item?.item_unit_price || 0) *
          parseFloat(item?.quantity || 0),
        0
      );
      const sot_total_amount = values.data.reduce(
        (total, item) => total + parseFloat(item?.item_total_amount || 0),
        0
      );

      const rounded_total = Math.round(sot_total_amount);
      const roundoff = (rounded_total - sot_total_amount).toFixed(2);

      // Update the totals
      setFieldValue("sot_total_gst", sot_total_gst.toFixed(2));
      setFieldValue("sot_total_discount", sot_total_discount.toFixed(2));
      setFieldValue("sot_sub_total", sot_sub_total.toFixed(2));
      setFieldValue("sot_total_amount", rounded_total.toFixed(2));
      setFieldValue("sot_round_off_value", roundoff);

      // Also recalculate invoice discount if it exists
      if (values?.header_inv_dis_per && values?.sot_is_invoice_dis_percentage) {
        const invoiceDiscount = (sot_total_amount * parseFloat(values.header_inv_dis_per)) / 100;
        setFieldValue("sot_invoice_discount", invoiceDiscount.toFixed(2));
        setFieldValue("sot_invoice_discount_percentage", values.header_inv_dis_per);
      } else if (values?.header_inv_dis_per && !values?.sot_is_invoice_dis_percentage) {
        const invoiceDiscount = parseFloat(values.header_inv_dis_per);
        const invoiceDiscountPercentage = sot_total_amount > 0 ? (invoiceDiscount / sot_total_amount) * 100 : 0;
        setFieldValue("sot_invoice_discount", invoiceDiscount.toFixed(2));
        setFieldValue("sot_invoice_discount_percentage", invoiceDiscountPercentage.toFixed(2));
      }
    }
  }, [values?.data, values?.header_inv_dis_per, values?.sot_is_invoice_dis_percentage, setFieldValue]);

  const onChangeHeaderDiscount = (
    discountPercentage,
    header_inv_dis_per,
    sot_is_invoice_dis_percentage,
    type = ""
  ) => {
    let updatedData = [...values?.data];
    if (type === "header") {
      const headerDiscount = parseFloat(discountPercentage || 0);
      if (!Array.isArray(values.data) || isNaN(headerDiscount)) return;

      updatedData = values.data.map((row) => {
        const quantity = parseFloat(row.quantity || 0);
        const unitPrice = parseFloat(row.item_unit_price || 0);
        const totalAmount = quantity * unitPrice;

        // If row has predefined discount, skip applying header discount
        if (row?.is_predefined_dis) {
          return row;
        }

        const discount = (totalAmount * headerDiscount) / 100;
        const taxPercentage = parseFloat(row.item_gst || 0);
        const tax = ((totalAmount - discount) * taxPercentage) / 100;

        const item_price_without_tax = (totalAmount - discount - tax).toFixed(
          2
        );
        const item_final_price = ((totalAmount - discount) / quantity).toFixed(
          2
        );

        return {
          ...row,
          item_discount_percentage: headerDiscount,
          item_discount_amount: discount.toFixed(2),
          item_tax_amount: tax.toFixed(2),
          item_price_wiithout_tax: item_price_without_tax,
          item_sgst: (tax / 2).toFixed(2),
          item_cgst: (tax / 2).toFixed(2),
          item_igst: 0,
          item_final_price: item_final_price,
          item_total_amount: (totalAmount - discount).toFixed(2),
        };
      });

      setFieldValue("data", updatedData);
    }

    // Update totals
    const sot_total_gst = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_tax_amount || 0),
      0
    );
    const sot_total_discount = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_discount_amount || 0),
      0
    );
    const sot_sub_total = updatedData.reduce(
      (subtotal, item) =>
        subtotal +
        parseFloat(item?.item_unit_price || 0) *
        parseFloat(item?.quantity || 0),
      0
    );

    const sot_total_without_inv_dis = updatedData.reduce(
      (total, item) => total + parseFloat(item.item_total_amount || 0),
      0
    );

    const sot_invoice_discount = sot_is_invoice_dis_percentage
      ? (sot_total_without_inv_dis * header_inv_dis_per) / 100
      : header_inv_dis_per;

    const sot_invoice_discount_percentage = sot_is_invoice_dis_percentage
      ? header_inv_dis_per
      : (header_inv_dis_per / sot_total_without_inv_dis) * 100;

    const sot_total_amount =
      updatedData.reduce(
        (total, item) => total + parseFloat(item.item_total_amount || 0),
        0
      ) - parseFloat(sot_invoice_discount);
    const rounded_total = Math.round(sot_total_amount);
    const roundoff = (rounded_total - sot_total_amount).toFixed(2);
    setFieldValue("sot_total_gst", sot_total_gst.toFixed(2));
    setFieldValue("sot_total_discount", sot_total_discount.toFixed(2));
    setFieldValue("sot_sub_total", sot_sub_total.toFixed(2));
    setFieldValue("sot_total_amount", rounded_total.toFixed(2));
    setFieldValue("sot_round_off_value", roundoff);
    setFieldValue("header_dis_per", "");
    setFieldValue("sot_invoice_discount", sot_invoice_discount?.toFixed(2));
    setFieldValue(
      "sot_invoice_discount_percentage",
      sot_invoice_discount_percentage
    );
  };
  const onDoctorSuccess = (data) => {
    console.log("doctorrrr", data);
    const options = [
      ...data.data.DoctorList.map((item) => {
        const parts = [
          `Dr ${item?.dr_name}`, // name is always there
          item?.dr_hospital || null,
          item?.dr_area || null,
          item?.dr_city || null,
        ].filter(Boolean); // remove null/undefined/empty

        return {
          value: item.dr_name, // Store only the doctor name
          label: parts.join(" - "), // Display formatted name
          id: item.dr_id,
        };
      }),
    ];
    console.log("updated doctor", options?.[0]);
    setFieldValue("doctor_name", options?.[0]);
  };
  const onDoctorError = (data) => {
    console.log("doctor--", data);
  };
  const { initialValues } = useFormikContext();
  console.log("ini", initialValues);

  const didRun = useRef(false);
  // useEffect(() => {
  //   if (!initialValues?.doctor_name) return;
  //   console.log("doctor-initial", initialValues.doctor_name);

  //   // if (!didRun.current) {
  //   dispatch(
  //     doctorTableAction(
  //       {
  //         pageNumber: 1,
  //         pageSize: 20,
  //         filterData: {
  //           inputValue: initialValues.doctor_name,
  //           selectColumn: "dr_name",
  //           sort_by: "",
  //           sort_order: "",
  //         },
  //       },
  //       onDoctorSuccess,
  //       onDoctorError
  //     )
  //   );
  //   // didRun.current = true;
  //   // }
  // }, [initialValues?.doctor_name]);
  useEffect(() => {
    if (!initialValues?.doctor_id) return;
    console.log("doctor-initial-id", initialValues.doctor_id);

    // if (!didRun.current) {
    dispatch(
      doctorTableAction(
        {
          pageNumber: 1,
          pageSize: 20,
          filterData: {
            inputValue: initialValues.doctor_id,
            selectColumn: "dr_id",
            sort_by: "",
            sort_order: "",
          },
        },
        onDoctorSuccess,
        onDoctorError
      )
    );
    // didRun.current = true;
    // }
  }, [initialValues?.doctor_id]);

  useEffect(() => {
    return () => {
      const nextPath = window.location.hash; // Use hash for hash-based routing
      if (
        formValuesRef?.current &&
        !formValuesRef?.current?.submittedButton &&
        !disabled
      ) {
        handleBilling({
          ...formValuesRef.current,
          submittedButton: "draft",
          save: "auto",
        });
      }
    };
  }, [navigate]);

  const loadOptions = (inputValue) => {
    return new Promise((resolve) => {
      const onSuccess = (data) => {
        const options = [
          ...data.data.DoctorList.map((item) => {
            const parts = [
              `Dr ${item?.dr_name}`, // name is always there
              item?.dr_hospital || null,
              item?.dr_area || null,
              item?.dr_city || null,
            ].filter(Boolean); // remove null/undefined/empty

            return {
              value: item.dr_name, // Store only the doctor name
              label: parts.join(" - "), // Display formatted name
              id: item.dr_id,
            };
          }),
          {
            value: { text: "new_doctor", number: inputValue },
            label: <span className="text-primary">+ New Doctor</span>,
          },
        ];
        setLocalData(data?.data?.DoctorList || []);
        resolve(options);
      };

      const onError = (error) => {
        setLocalData([]);
        resolve([
          {
            value: { text: "new_doctor", number: inputValue },
            label: <span className="text-primary">+ New Doctor</span>,
          },
        ]);
      };

      if (inputValue?.length > 0) {
        console.log("...", values, inputValue);
        dispatch(
          doctorTableAction(
            {
              pageNumber: 1,
              pageSize: 20,
              filterData: {
                inputValue: inputValue,
                selectColumn: "dr_name",
                sort_by: "",
                sort_order: "",
              },
            },
            onSuccess,
            onError
          )
        );
      } else {
        resolve([]);
      }
    });
  };
  // const scheduledDrugValidation = (values) => {
  //   console.log("......", values);
  //   let hasError = false;
  //   let errorMessages = [];

  //   const ordinalSuffixes = ["st", "nd", "rd"];
  //   let scheduledList = [];
  //   values?.data?.forEach((item, index) => {
  //     if (
  //       item.item_schedule &&
  //       (item?.item_schedule !== "" ||
  //         item?.item_schedule !== null ||
  //         item?.item_schedule !== undefined)
  //     ) {
  //       scheduledList.push({
  //         itemNumber: index + 1,
  //         itemName: item.item_name,
  //       });
  //     }
  //   });
  //   console.log(
  //     "###############",
  //     scheduledList.length > 0,
  //     values?.sot_prescription === "" ||
  //       values?.sot_prescription === null ||
  //       values?.sot_prescription === undefined,
  //     scheduledList.length > 0 &&
  //       (values?.sot_prescription === "" ||
  //         values?.sot_prescription === null ||
  //         values?.sot_prescription === undefined)
  //   );
  //   if (
  //     scheduledList.length > 0 &&
  //     (values?.sot_prescription === "" ||
  //       values?.sot_prescription === null ||
  //       values?.sot_prescription === undefined)
  //   ) {
  //     console.log("values.sot_prescription", values.sot_prescription);
  //     hasError = true;
  //     errorToast(
  //       `Prescripction is required for ${scheduledList
  //         .map((item, index) => item?.itemName)
  //         .join(",")}`
  //     );
  //   }
  //   return hasError;
  // };
  const handleDraft = (selectedOption) => {
    console.log(values, "fgfdgdfgfdgdfgfdgfdg");

    let actionType = invoiceDraftCreateAction;
    // if (values.submittedButton === "draft") {
    // actionType = invoiceDraftCreateAction;

    if (page === "edit") {
      actionType = invoiceEditAction;
    }
    const filteredData = values?.data;
    if (
      filteredData.some(
        (item) => parseFloat(item?.item_margin_percentage) < 0
      ) &&
      values?.submittedButton !== "draft"
    ) {
      errorToast("Margin is negative, please check");
    } else {
      if (values?.data?.length > 0) {
        setTimeout(() => {
          setLoading(true);
          dispatch(
            actionType(
              {
                orderData: {
                  is_draft_order:
                    values.submittedButton === "draft" ? true : false,
                  store_id: values?.store_id || storeId || "",
                  cmr_id: values?.cmr_id || "",
                  cmr_phone_number: parseInt(values?.phone?.label),
                  sot_total_gst: values?.sot_total_gst || 0,
                  sot_total_discount: values?.sot_total_discount || 0,
                  sot_payment_status:
                    values.submittedButton === "draft" ? "pending" : "paid",
                  sot_transaction_id: "",
                  sot_billing_address: "",
                  sot_remarks: values?.sot_remarks,
                  sot_sub_total: values?.sot_sub_total || 0,
                  sot_total_amount: values?.sot_total_amount || 0,
                  sot_round_off_value: values?.sot_round_off_value || 0,
                  doctor_name: values?.doctor_name?.value,
                  sot_is_invoice_dis_percentage:
                    values?.sot_is_invoice_dis_percentage,
                  sot_invoice_discount: values?.sot_invoice_discount,
                  sot_invoice_discount_percentage:
                    values?.sot_invoice_discount_percentage,
                  sot_prescription: values?.sot_prescription,
                  ...(values?.sot_id ? { sot_id: values?.sot_id } : {}),
                  sot_is_store_pick_up: values?.sot_is_store_pick_up,
                  sot_custom_invoice_date:
                    values?.sot_custom_invoice_date || null,
                  sot_custom_invoice_number:
                    values?.sot_custom_invoice_number || "",
                  sot_pay_to_address: values.sot_pay_to_address,
                  sot_ship_to_address: values.sot_ship_to_address,
                },
                paymentData: values?.paymentData?.map((pay) => ({
                  payment_mode_name: pay?.payment_mode_name,
                  payment_amount: pay?.payment_amount,
                  transaction_id: pay?.transaction_id || "",
                })),
                itemData: values?.data?.map((item, index) => ({
                  item_code: item?.item_code,
                  item_generic_name: item?.item_generic_name,
                  item_name: item?.item_name,
                  item_quantity: parseInt(item?.quantity),
                  item_pack_qty: item?.item_pack_qty || 0,
                  item_loose_qty: item?.item_loose_qty || 0,
                  item_free_quantity:
                    parseInt(item?.item_free_quantity ?? 0, 10) || 0,
                  item_uom: item?.item_uom,
                  item_batch_number: item?.item_batch_number,
                  item_exp_date: moment(item?.item_exp_date).format(
                    "YYYY-MM-DD"
                  ),
                  item_rack_location: item?.item_rack_location,
                  item_schedule: item?.item_schedule,
                  item_hsn: item?.item_hsn,
                  item_unit_price: parseFloat(item?.item_unit_price),
                  item_price_wiithout_tax: parseFloat(
                    item?.item_price_wiithout_tax
                  ),
                  item_discount_amount: parseFloat(item?.item_discount_amount),
                  item_discount_percentage:
                    parseFloat(item?.item_discount_percentage) || 0,
                  item_tax_amount: parseFloat(item?.item_tax_amount),
                  item_total_amount: parseFloat(
                    parseFloat(item.item_unit_price) * parseInt(item.quantity) -
                    parseFloat(item.item_discount_amount)
                  ).toFixed(2),
                  item_id: item?.item_id,
                  item_gst: parseFloat(item?.item_gst),
                  item_sgst: parseFloat(item?.item_sgst),
                  item_cgst: parseFloat(item?.item_cgst),
                  item_total_tax_percentage: parseFloat(
                    item?.item_total_tax_percentage
                  ),
                  item_pack_price: item?.item_pack_price || 0,
                  // item_manufacturer_id: item?.item_manufacturer_id ?? "",
                  // item_manufacturer_name: item?.item_manufacturer_name ?? "",
                })),
              },
              (data) => {
                setLoading(false);
                // resetForm();
                // setPaymentPanel(false);
                // if (values.submittedButton === "print") {
                //   handlea4Print(
                //     data?.data?.orderData?.[0]?.sot_id,
                //     "Billing",
                //     dispatch
                //   );

                //Needed for unwrap
                // handlePrint(data?.data, storeDetails);
                // }
                successToast(data?.message);
                setLoading(true);
                setTimeout(() => {
                  setLoading(false);
                }, 50);

                navigate(
                  `/add-doctor?doctor=${selectedOption?.value?.number?.toString() || ""
                  }&draft=${data?.data?.orderData?.[0]?.sot_id}`
                );

                formValuesRef.current = null; // Reset after handling
              },
              (data) => {
                errorToast(data?.data?.message);
              }
            )
          );
        }, 500); // Timeout set to 100ms
      }
    }
  };
  return (
    <div className="h-[8rem]">
      <BillingUploadPrescription
        open={uploadPrescription || uploadPrescriptionView}
        setOpen={
          uploadPrescriptionView
            ? setUploadPrescriptionView
            : setUploadPrescription
        }
        isView={uploadPrescriptionView}
      />
      {console.log("view====", values)}
      <div className="h-[4rem] border-y border-[var(--create-table-footer-border)] flex items-center justify-between">
        <div className="flex w-[80%] h-full">
          {billCreateInputs?.find((inp) => inp?.value === "remarks")
            ?.enabled && (
              <div className="w-[25%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex items-center">
                <ItemsInputField
                  disabled={false}
                  name={`sot_remarks`}
                  isLabelReq={false}
                  placeholder="Enter your remarks here..."
                />
              </div>
            )}

          {(billCreateInputs?.find((inp) => inp?.value === "doctor")?.enabled ||
            billCreateInputs?.find((inp) => inp?.value === "sot_prescription")
              ?.enabled) && (
              <div className="w-[50%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex items-center gap-3 justify-between">
                {billCreateInputs?.find((inp) => inp?.value === "doctor")
                  ?.enabled && (
                    <div className="w-[50%]">
                      <TextSearchField
                        isLabelReq={false}
                        isRequired={false}
                        disabled={disabled || page === "view"}
                        maxlength={50} // Changed from 10 to be more reasonable for names
                        label="Doctor"
                        className="z-20"
                        isValidNewOption={true}
                        // autoFocus={true}
                        name="doctor_name"
                        placeholder="Enter Doctor Name"
                        entityType="Doctor"
                        addNewRoute="/add-doctor"
                        loadOptions={loadOptions}
                        onChange={(selectedOption) => {
                          if (
                            selectedOption === "new_doctor" ||
                            selectedOption?.value?.text === "new_doctor"
                          ) {
                            if (values.cmr_id === "") {
                              errorToast("select customer before creating Doctor");
                            } else if (values.data.length === 0) {
                              errorToast("Add product before creating Doctor");
                            } else if (values.sot_custom_invoice_date === null) {
                              errorToast(
                                "Select invoice data before creating Doctor"
                              );
                            } else {
                              handleDraft(selectedOption);
                              // navigate(
                              //   `/add-doctor?doctor=${
                              //     selectedOption?.value?.number?.toString() || ""
                              //   }`
                              // );
                            }
                          } else {
                            console.log("selectedOption", selectedOption);
                            setFieldValue("doctor_name", selectedOption); // Set the selected doctor

                            // Find the doctor data using the correct ID field
                            const doctorData = localData?.find(
                              (item) => item?.dr_id === selectedOption?.value // Changed from cmr_id to dr_id
                            );

                            console.log("Selected doctor data:", doctorData); // Debug log

                            if (
                              searchRef &&
                              searchRef.current &&
                              selectedOption?.value
                            ) {
                              searchRef.current.focus();
                            }
                          }
                        }}
                        onCreateOption={() => { }}
                      />
                    </div>
                  )}
                {billCreateInputs?.find(
                  (inp) => inp?.value === "sot_prescription"
                )?.enabled && (
                    <>
                      <div className="w-[50%]">
                        <AddButton
                          isDisabled={disabled}
                          onclkFn={() => {
                            console.log("check my values",values);
                            setUploadPrescription(true);
                          }}
                          icon={<UploadIcon fill="var(--upload-button-fg)" />}
                          prefixIcon={true}
                          text={
                            values?.sot_prescription?.length > 0 && values.sot_prescription[0] !== ""
                              ? "Re-Upload Prescription"
                              : "Upload Prescription"
                          }
                          className="
                            flex items-center px-[10px] h-[44px] w-full whitespace-nowrap rounded-md gap-[8px]
                            bg-[var(--upload-button-bg)] cursor-pointer
                            text-[var(--upload-button-fg)]
                            border border-[var(--upload-button-border)]
                            text-[length:var(--upload-button-fs)] 
                            font-[var(--upload-button-fw)]
                        "
                        />
                      </div>
                      {values?.sot_prescription?.length > 0 && values.sot_prescription[0] !== "" && (
                        <div className="w-[10%]">
                          <AddButton
                            onclkFn={() => {
                              setUploadPrescriptionView(true);
                            }}
                            icon={<ViewIcon fill="var(--view-button-fg)" />}
                            prefixIcon={true}
                            className={`flex items-center justify-center  h-[44px] w-[3rem] whitespace-nowrap rounded-md gap-[8px]
           bg-[var(--view-button-bg)] ${values?.sot_prescription?.length > 0 && values.sot_prescription[0] !== "" ? "cursor-pointer" : "cursor-not-allowed"
                              } 
           text-[var(--view-button-fg)]
           border border-[var(--view-button-border)]`}
                          />
                        </div>
                      )}
                    </>
                  )}
              </div>
            )}
          {(billCreateInputs?.find((inp) => inp?.value === "header_dis_per")
            ?.enabled ||
            billCreateInputs?.find((inp) => inp?.value === "header_inv_dis_per")
              ?.enabled) && (
              <div className="w-[25%] px-5 border-r border-r-[var(--create-table-footer-border)] h-full flex gap-3 items-center">
                {page !== "view" &&
                  billCreateInputs?.find((inp) => inp?.value === "header_dis_per")
                    ?.enabled && (
                    <div className="w-[50%]">
                      <ItemsInputField
                        disabled={disabled}
                        name={`header_dis_per`}
                        isLabelReq={false}
                        placeholder="Header Disc %"
                        onKeyDown={(event) => {
                          if (event.key === "Tab" || event.key === "Enter") {
                            onChangeHeaderDiscount(
                              parseFloat(event.target.value),
                              parseFloat(values?.header_inv_dis_per || 0),
                              values?.sot_is_invoice_dis_percentage,
                              "header"
                            );
                            header_inv_dis_per_ref?.current.focus();
                          }
                        }}
                        onBlur={(event) => {
                          onChangeHeaderDiscount(
                            parseFloat(event.target.value),
                            parseFloat(values?.header_inv_dis_per || 0),
                            values?.sot_is_invoice_dis_percentage
                          );
                        }}
                      />
                    </div>
                  )}
                {billCreateInputs?.find(
                  (inp) => inp?.value === "header_inv_dis_per"
                )?.enabled && (
                    <div
                      className={`${page === "view" ? "w-[100%]" : "w-[50%]"
                        }  relative`}
                    >
                      <ItemsInputField
                        ref={header_inv_dis_per_ref}
                        disabled={disabled || values?.data?.length === 0}
                        name={`header_inv_dis_per`}
                        isLabelReq={false}
                        placeholder={`Inv Disc ${values?.sot_is_invoice_dis_percentage ? "%" : "₹"
                          }`}
                        onKeyDown={(event) => {
                          if (event.key === "Tab" || event.key === "Enter") {
                            let val = parseFloat(event.target.value || 0);
                            const sot_total_without_inv_dis =
                              parseFloat(
                                values?.data?.reduce(
                                  (total, item) =>
                                    total + parseFloat(item.item_total_amount || 0),
                                  0
                                )
                              ) || 0;

                            if (values?.sot_is_invoice_dis_percentage) {
                              // Percentage mode
                              const min = parseFloat(user_discount?.min) || 0;
                              const max = parseFloat(user_discount?.max) || 100;
                              if (val < min) {
                                errorToast(
                                  `Invoice discount cannot be less than ${min}%. Setting to minimum.`
                                );
                                setFieldValue("header_inv_dis_per", min);
                                val = min;
                              } else if (val > max) {
                                errorToast(
                                  `Invoice discount cannot be more than ${max}%. Setting to maximum.`
                                );
                                setFieldValue("header_inv_dis_per", max);
                                val = max;
                              }
                            } else {
                              // Amount mode: convert to percentage and validate
                              const minPer = parseFloat(user_discount?.min) || 0;
                              const maxPer = parseFloat(user_discount?.max) || 100;
                              const percent =
                                sot_total_without_inv_dis > 0
                                  ? (val / sot_total_without_inv_dis) * 100
                                  : 0;
                              if (percent < minPer) {
                                errorToast(
                                  `Invoice discount cannot be less than ${minPer}% of subtotal. Setting to minimum.`
                                );
                                const minVal =
                                  (minPer / 100) * sot_total_without_inv_dis;
                                setFieldValue("header_inv_dis_per", minVal);
                                val = minVal;
                              } else if (percent > maxPer) {
                                errorToast(
                                  `Invoice discount cannot be more than ${maxPer}% of subtotal. Setting to maximum.`
                                );
                                const maxVal =
                                  (maxPer / 100) * sot_total_without_inv_dis;
                                setFieldValue("header_inv_dis_per", maxVal);
                                val = maxVal;
                              }
                            }
                            onChangeHeaderDiscount(
                              parseFloat(values?.header_dis_per || 0),
                              val,
                              values?.sot_is_invoice_dis_percentage
                            );
                          }
                        }}
                        onBlur={(event) => {
                          let val = parseFloat(event.target.value || 0);
                          const sot_total_without_inv_dis =
                            parseFloat(
                              values?.data?.reduce(
                                (total, item) =>
                                  total + parseFloat(item.item_total_amount || 0),
                                0
                              )
                            ) || 0;
                          if (values?.sot_is_invoice_dis_percentage) {
                            // Percentage mode
                            const min = parseFloat(user_discount?.min) || 0;
                            const max = parseFloat(user_discount?.max) || 100;
                            if (val < min) {
                              setFieldValue("header_inv_dis_per", min);
                              val = min;
                            } else if (val > max) {
                              setFieldValue("header_inv_dis_per", max);
                              val = max;
                            }
                          } else {
                            // Amount mode: convert to percentage and validate
                            const minPer = parseFloat(user_discount?.min) || 0;
                            const maxPer = parseFloat(user_discount?.max) || 100;
                            const percent =
                              sot_total_without_inv_dis > 0
                                ? (val / sot_total_without_inv_dis) * 100
                                : 0;
                            if (percent < minPer) {
                              const minVal =
                                (minPer / 100) * sot_total_without_inv_dis;
                              setFieldValue("header_inv_dis_per", minVal);
                              val = minVal;
                            } else if (percent > maxPer) {
                              const maxVal =
                                (maxPer / 100) * sot_total_without_inv_dis;
                              setFieldValue("header_inv_dis_per", maxVal);
                              val = maxVal;
                            }
                          }
                          onChangeHeaderDiscount(
                            parseFloat(values?.header_dis_per || 0),
                            val,
                            values?.sot_is_invoice_dis_percentage
                          );
                        }}
                      />
                      <button
                        disabled={disabled}
                        onClick={() => {
                          setFieldValue(
                            "sot_is_invoice_dis_percentage",
                            !values?.sot_is_invoice_dis_percentage
                          );
                          setFieldValue("header_inv_dis_per", "");
                          onChangeHeaderDiscount(
                            parseFloat(values?.header_dis_per),
                            // parseFloat(values?.header_inv_dis_per),
                            0,
                            !values?.sot_is_invoice_dis_percentage
                          );
                        }}
                        type="button"
                        className="absolute top-1/2 -translate-y-1/2 right-0 w-[40px] min-h-[40px] flex items-center justify-center bg-[var(--create-tab-foot-subTot-bg)] border border-[var(--items-search-input-border)] rounded-r"
                      >
                        {values?.sot_is_invoice_dis_percentage ? "%" : "₹"}
                      </button>
                    </div>
                  )}
              </div>
            )}
        </div>

        <div
          className="justify-between w-[20%] px-5 h-full flex flex-col items-center
      bg-[var(--create-table-footer-totals-bg)] text-[var(--create-table-footer-totals-fg)]
      text-[length:var(--create-table-footer-totals-fs)] font-[var(--create-table-footer-totals-fw)] relative"
        >
          {/* Toggle Button */}
          {!isOpen && (
            <div
              className="absolute top-[-1.2rem] left-1/2 transform -translate-x-1/2 bg-[var(--create-table-footer-totals-bg)] cursor-pointer 
                    flex items-start justify-center"
              style={{
                width: "80px",
                height: "55px",
                borderRadius: "9999px", // makes it oval
                padding: "0.2rem",
              }}
              onClick={() => setIsOpen(!isOpen)}
            >
              {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
            </div>
          )}
          {isOpen && (
            <div
              className="p-5 flex flex-col w-full space-y-5 absolute bottom-[100%]"
              style={{
                backgroundColor: "var(--create-tab-foot-subTot-bg)",
                color: "var(--create-tab-foot-subTot-fg)",
                fontSize: "var(--create-tab-foot-subTot-fs)",
                fontWeight: "var(--create-tab-foot-subTot-fw)",
              }}
            >
              <div className="flex justify-between">
                <span>Sub Total</span>
                <span>₹{values?.sot_sub_total || "0.00"}</span>
              </div>
              <div className="flex justify-between">
                <span>Discount</span>
                <span style={{ color: "var(--success-color)" }}>
                  {"- "}₹{values?.sot_total_discount || "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Invoice Discount</span>
                <span style={{ color: "var(--success-color)" }}>
                  {"- "}₹{values?.sot_invoice_discount || "0.00"}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Round off</span>
                <span
                  className={
                    parseFloat(values?.sot_round_off_value || 0) > 0
                      ? "text-[var(--error-color)]"
                      : parseFloat(values?.sot_round_off_value || 0) < 0
                        ? "text-[var(--success-color)]"
                        : ""
                  }
                >
                  ₹{parseFloat(values?.sot_round_off_value || 0).toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span>Tax</span>
                <span style={{ color: "var(--error-color)" }}>
                  ₹{values?.sot_total_gst || "0.00"}
                </span>
              </div>
              <div
                className={`absolute top-[-2.3rem] left-1/2 transform -translate-x-1/2 bg-[var(--create-tab-foot-subTot-bg)] cursor-pointer 
              flex items-start justify-center `}
                style={{
                  width: "80px",
                  height: "55px",
                  borderRadius: "9999px", // makes it oval
                  padding: "0.2rem",
                  zIndex: "",
                }}
                onClick={() => setIsOpen(!isOpen)}
              >
                {isOpen ? <ChevronDown size={18} /> : <ChevronUp size={18} />}
              </div>
            </div>
          )}

          {/* Total Amount */}
          <div className="flex justify-between w-full h-full items-center">
            <span>Total</span>
            <span>₹{values?.sot_total_amount || "0.00"}</span>
          </div>
        </div>
      </div>

      <div className="h-[4rem] flex justify-between px-5">
        <div></div>
        <div className="flex gap-3 items-center">
          {!disabled && (
            <AddButton
              prefixIcon={false}
              text={"Draft"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--draft-button-bg)] cursor-pointer
text-[var(--draft-button-fg)]
border border-[var(--draft-button-border)]
text-[length:var(--draft-button-fs)] 
font-[var(--draft-button-fw)]
"
              type="submit"
              onclkFn={() => {
                setFieldValue("submittedButton", "draft");
                if (Object.keys(errors).length > 0) {
                  handleShowErrorToast(errors);
                }
              }}
            />
          )}
          {console.log("::", values?.submittedButton)}
          <AddButton
            prefixIcon={false}
            text={disabled ? "View Payment Method" : "Proceed to Payment"}
            className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-create-fs)] 
font-[var(--primary-button-font-weight)]
"
            type="submit"
            onclkFn={async () => {
              // const validate = scheduledDrugValidation(values);
              // console.log("!!!!", !validate);
              // if (!validate) {
              setFieldValue("submittedButton", "proceed");

              const formErrors = await validateForm();
              if (Object.keys(formErrors).length > 0) {
                handleShowErrorToast(formErrors);
              }
              // }
            }}
          />
          {disabled && values?.sot_order_status === "draft" && (
            <AddButton
              prefixIcon={false}
              text={"EDIT"}
              className="
flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px]
bg-[var(--primary-button-bg-color)] cursor-pointer
text-[var(--primary-button-fg-color)]
border border-[var(--primary-button-bg-color)]
hover:bg-[var(--primary-button-hover-color)]
text-[length:var(--primary-button-font-size)] 
font-[var(--primary-button-font-weight)]
"
              type="button"
              onclkFn={() => {
                navigate(`/edit-bill/${values?.sot_id}`);
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default BillingFooterSection;
